import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { DateTime } from 'luxon';
import { capitalizeFirstLetter } from '@getaccept/lib-shared/src/helpers';
import { DealroomEventType, ParticipantCreationSource } from '../../api/timeline/types/log.types';
import type {
  ContextualCommentReplyNotificationEvent,
  ContextualCommentUpdatedNotificationEvent,
  DealroomNotificationEvent,
  FileUploadedNotificationEvent,
  MessageCreatedNotificationEvent,
  Notification,
  ParticipantCreatedNotificationEvent,
  TaskCreatedNotificationEvent,
  TaskUpdatedNotificationEvent,
  VisitedNotificationEvent,
  NotificationEvent,
} from '../types/notification.types';
import AvatarHelper from '../../helpers/avatar.helper';
import { TaskStatus } from '../../mutual-action-plan/types/enums/task-status';
import { DrView } from '../../types/dr-view';
import type { Participant } from '../../types/participant';
import { CommentStatus } from '../../contextual-commenting/types/comment-status';

type IncludedEvents =
  | DealroomEventType.FileUploaded
  | DealroomEventType.Visited
  | DealroomEventType.ContextualCommentUpdated
  | DealroomEventType.ContextualCommentReplyCreated
  | DealroomEventType.ConversationMessageCreated
  | DealroomEventType.TaskUpdated
  | DealroomEventType.TaskCreated
  | DealroomEventType.ParticipantCreated;

export default class NotificationHelper {
  private static eventTypeToObject: Record<
    IncludedEvents,
    (
      event: DealroomNotificationEvent
    ) => Omit<NotificationEvent, 'id' | 'createdAt' | 'type' | 'isRead'>
  > = {
    [DealroomEventType.FileUploaded]: (event: FileUploadedNotificationEvent) => ({
      participantId: event.participantId,
      eventName: t('file_upload'),
      eventTitle: event.fileData?.name || '',
      metaData: {
        fileId: event.fileId,
      },
    }),
    [DealroomEventType.Visited]: (event: VisitedNotificationEvent) => ({
      participantId: event.participantId,
      eventName: event.isFirstVisit ? t('dr_activity_visited_first_time') : t('dr_visited_again'),
    }),
    [DealroomEventType.ContextualCommentUpdated]: (
      event: ContextualCommentUpdatedNotificationEvent
    ) => {
      const eventName =
        event.updatedData.status === CommentStatus.Active
          ? t('dr_comment_created')
          : event.updatedData.status === CommentStatus.Resolved
            ? t('dr_activity_owner_resolved_comment')
            : '';

      return {
        participantId: event.participantId,
        eventName,
        metaData: {
          commentId: event.commentId,
        },
      };
    },
    [DealroomEventType.ContextualCommentReplyCreated]: (
      event: ContextualCommentReplyNotificationEvent
    ) => ({
      participantId: event.participantId,
      eventName: t('reply_thread'),
      metaData: {
        commentId: event.commentId,
        replyId: event.replyId,
      },
    }),
    [DealroomEventType.ConversationMessageCreated]: (event: MessageCreatedNotificationEvent) => ({
      participantId: event.participantId,
      eventName: t('wrote_in_chat'),
      metaData: {
        conversationId: event.conversationId,
        messageId: event.messageId,
      },
    }),
    [DealroomEventType.TaskUpdated]: (event: TaskUpdatedNotificationEvent) => {
      if (
        event.update.status &&
        capitalizeFirstLetter(event.update.status) === TaskStatus.Completed
      ) {
        return {
          participantId: event.participantId,
          eventName: t('task_complete'),
          eventTitle: event.taskData?.title || '',
          metaData: {
            actionPlanId: event.actionPlanId,
            milestoneId: event.milestoneId,
            taskId: event.taskId,
          },
        };
      }
      if (event.update.participantIds.length) {
        return {
          participantId: event.participantId,
          eventName: t('task_assigned'),
          eventTitle: event.taskData?.title || '',
          metaData: {
            actionPlanId: event.actionPlanId,
            milestoneId: event.milestoneId,
            taskId: event.taskId,
          },
        };
      }
      return undefined;
    },
    [DealroomEventType.TaskCreated]: (event: TaskCreatedNotificationEvent) => {
      if (event.taskData?.participantIds.length) {
        return {
          participantId: event.participantId,
          eventName: t('task_assigned'),
          eventTitle: event.taskData?.title || '',
        };
      }
      return undefined;
    },
    [DealroomEventType.ParticipantCreated]: (event: ParticipantCreatedNotificationEvent) => {
      if (event.creationSource === ParticipantCreationSource.UniversalLink) {
        return {
          participantId: event.createdParticipantId,
          eventName: t('dr_participant_joined_universal_link'),
        };
      }
      return {
        participantId: event.participantId,
        eventName: `${t('dr_invited_participant_card_event', {
          firstName: event.createdParticipantData?.firstName || '',
          lastName: event.createdParticipantData?.lastName || event.createdParticipantData.email,
        })}`,
      };
    },
  };

  static getMappedNotifications(notifications: Notification[]): NotificationEvent[] {
    const notificationsWithMetaData = notifications.filter(notification => {
      const getNotificationMetaData = this.eventTypeToObject[notification.data.event.type];
      if (!getNotificationMetaData) return false;

      const notificationMetaData = getNotificationMetaData(notification.data.event);

      return !!notificationMetaData;
    });

    return notificationsWithMetaData.map(notification => {
      const getNotificationMetaData = this.eventTypeToObject[notification.data.event.type];
      const notificationMetaData = getNotificationMetaData(notification.data.event);

      const mappedNotification: NotificationEvent = {
        id: notification.id,
        type: notification.data.event.type,
        createdAt: notification.createdAt,
        isRead: !!notification.viewedAt,
        ...notificationMetaData,
      };

      return mappedNotification;
    });
  }

  static organizeNotificationsByDate(
    notifications: NotificationEvent[]
  ): { date: string; notifications: NotificationEvent[] }[] {
    return notifications.reduce((acc, notification) => {
      const dateKey = DateTime.fromMillis(notification.createdAt).toISODate();

      const index = acc.findIndex(item => item.date === dateKey);
      if (index === -1) {
        acc.push({ date: dateKey, notifications: [notification] });
      } else {
        acc[index].notifications.push(notification);
      }
      return acc;
    }, []);
  }

  static getRouterLocationData(notification: NotificationEvent) {
    switch (notification.type) {
      case DealroomEventType.TaskCreated:
      case DealroomEventType.TaskUpdated:
        return {
          params: {
            view: DrView.ActionPlan,
            itemId: notification.metaData?.milestoneId,
          },
          query: { taskId: notification.metaData?.taskId },
        };
      case DealroomEventType.MilestoneCreated:
        return {
          params: {
            view: DrView.ActionPlan,
            itemId: notification.metaData?.milestoneId,
          },
          query: {
            taskId: undefined,
            messageId: undefined,
            commentId: undefined,
            fileId: undefined,
          },
        };
      case DealroomEventType.ConversationMessageCreated:
        return {
          params: {
            view: DrView.Chat,
            itemId: notification.metaData?.conversationId,
          },
          query: {
            taskId: undefined,
            messageId: notification.metaData?.messageId,
            commentId: undefined,
            fileId: undefined,
          },
        };
      case DealroomEventType.FileUploaded:
        return {
          params: {
            view: DrView.Files,
            itemId: undefined,
          },
          query: {
            taskId: undefined,
            messageId: undefined,
            commentId: undefined,
            fileId: notification.metaData?.fileId,
          },
        };
      case DealroomEventType.ContextualCommentReplyCreated:
      case DealroomEventType.ContextualCommentUpdated: {
        return {
          params: {
            view: DrView.Content,
            itemId: undefined,
          },
          query: {
            taskId: undefined,
            messageId: undefined,
            commentId: notification.metaData?.commentId,
            fileId: undefined,
          },
        };
      }
      default:
        return {
          params: {
            view: DrView.Content,
            itemId: undefined,
          },
          query: {
            taskId: undefined,
            messageId: undefined,
            commentId: undefined,
            fileId: undefined,
          },
        };
    }
  }

  static replaceParticipantIdsWithNames = (text: string, participants: Participant[]) => {
    const participantIdRegex = /<participantId:([a-zA-Z0-9-]+)>/g;

    return text.replace(participantIdRegex, (_, participantId) => {
      const participant = participants.find(({ id }) => id === participantId);
      const mention = participant
        ? AvatarHelper.getDisplayName(participant, false)
        : t('dr_chat_removed_participant');

      return `<span class="mention">@${mention}</span>`;
    });
  };

  static formatPusherTypeToDealroomEventType = (type: string) => {
    switch (type) {
      case 'conversation_message_created':
        return DealroomEventType.ConversationMessageCreated;
      case 'contextual_comment_reply_created':
        return DealroomEventType.ContextualCommentReplyCreated;
      case 'contextual_comment_updated':
        return DealroomEventType.ContextualCommentUpdated;
      case 'file_uploaded':
        return DealroomEventType.FileUploaded;
      case 'visited':
        return DealroomEventType.Visited;
      case 'task_updated':
        return DealroomEventType.TaskUpdated;
      case 'task_created':
        return DealroomEventType.TaskCreated;
      case 'participant_created':
        return DealroomEventType.ParticipantCreated;
      default:
        return type;
    }
  };

  static getNotificationsWithMentionNames = (
    notifications: NotificationEvent[],
    participants: Participant[]
  ) =>
    notifications.map(notification => {
      if (
        [
          DealroomEventType.ContextualCommentReplyCreated,
          DealroomEventType.ContextualCommentUpdated,
        ].includes(notification.type)
      ) {
        return {
          ...notification,
          metaData: {
            ...notification.metaData,
            content: NotificationHelper.replaceParticipantIdsWithNames(
              notification.metaData.content,
              participants
            ),
          },
        };
      }
      return notification;
    });
}
